import { useEffect, useRef } from 'react';

interface UseInsightChatHandoffArgs {
  open: boolean;
  // Question handed off from the Help center; cleared by the caller once sent.
  pendingQuestion?: string;
  onPendingQuestionSent?: () => void;
  sessionId: string | null;
  // True once the drawer's own session restore/create has settled.
  sessionReady: boolean;
  hasMessages: boolean;
  awaitingReply: boolean;
  startNewChat: () => void;
  send: (question: string) => void;
}

// Help center -> Insight Chat hand-off (PRD Phase 7): sends the question once,
// in a fresh chat. An empty chat counts as fresh; otherwise a new one starts.
export const useInsightChatHandoff = ({
  open,
  pendingQuestion,
  onPendingQuestionSent,
  sessionId,
  sessionReady,
  hasMessages,
  awaitingReply,
  startNewChat,
  send,
}: UseInsightChatHandoffArgs) => {
  const handledQuestionRef = useRef<string | null>(null);
  // The chat that was open when the question arrived; it must not receive it.
  const staleSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingQuestion) {
      handledQuestionRef.current = null;
      return;
    }
    if (
      !open ||
      !sessionReady ||
      handledQuestionRef.current === pendingQuestion
    ) {
      return;
    }
    handledQuestionRef.current = pendingQuestion;
    staleSessionIdRef.current = hasMessages ? sessionId : null;
    if (hasMessages) startNewChat();
    // startNewChat is recreated each render; run once per question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionReady, pendingQuestion]);

  useEffect(() => {
    if (
      !open ||
      !pendingQuestion ||
      handledQuestionRef.current !== pendingQuestion ||
      !sessionReady ||
      !sessionId ||
      sessionId === staleSessionIdRef.current ||
      hasMessages ||
      awaitingReply
    ) {
      return;
    }
    send(pendingQuestion);
    onPendingQuestionSent?.();
    // send is recreated each render; these are the readiness inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    pendingQuestion,
    sessionReady,
    sessionId,
    hasMessages,
    awaitingReply,
  ]);
};

export default useInsightChatHandoff;
